import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRight, Eye, Folder } from 'lucide-react';
import { useProjects } from '../../hooks/queries/useProjects';
import { Project } from '../../hooks/queries/projectTypes';
import { useNavigate } from 'react-router-dom';
import NotificationCenter from '../notifications/NotificationCenter';
import { useTopicList } from '../../contexts/TopicListContext';
import { logger } from '../../utils/logger';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { useTopicUIStore } from '../../store/topicUIStore';

// Add a unique style block for the custom animation with scoped class
const slideFromRightKeyframes = `
@keyframes collapsedSidebarSlideFromRight {
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}
`;

// Inject the keyframes into the document only once
if (typeof document !== 'undefined' && !document.getElementById('collapsed-sidebar-animations')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'collapsed-sidebar-animations';
  styleElement.textContent = slideFromRightKeyframes;
  document.head.appendChild(styleElement);
}

interface CollapsedSidebarProps {
  project?: Project;
  onExpand: () => void;
  onProjectSelect?: (projectId: string) => void;
}

export function CollapsedSidebar({ project, onExpand, onProjectSelect }: CollapsedSidebarProps) {
  // Use React Query hook instead of Zustand store
  const { data: projects = [] } = useProjects(false); // false means show non-archived projects
  const { setActiveTopicId } = useTopicUIStore();
  const { expandTopicList } = useTopicList();
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  // State to track if the sidebar is being hovered
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const navigate = useNavigate();
  const location = window.location.pathname;
  const isDashboardActive = location.includes('/dashboard');
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Always get all non-archived projects, but apply animations to them
  const nonArchivedProjects = useMemo(() => {
    return projects.filter(p => !p.archived);
  }, [projects]);

  const handleDashboardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset active topic ID before navigating to dashboard
    setActiveTopicId(null);
    navigate('/dashboard');
  };

  const handleProjectClick = (projectId: string) => {
    // If the project is already selected, do nothing to prevent unnecessary reloading
    if (project?.id === projectId) {
      return;
    }

    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  const handleExpand = () => {
    logger.component('CollapsedSidebar: Expanding sidebar and topic list');
    // Expand topic list when expanding sidebar
    expandTopicList();
    // Call the original onExpand function
    onExpand();
  };

  return (
    <div
      className="h-full flex flex-col relative"
      onMouseEnter={() => setIsSidebarHovered(true)}
      onMouseLeave={() => setIsSidebarHovered(false)}
      style={{ contain: 'layout' }}
    >
      {/* Remove shadow overlay */}

      {/* Logo and content - moved header to top section */}
      <div className="flex-1 flex flex-col items-center">
        <div
          className="w-full flex flex-col items-center py-2"
          style={{
            paddingTop: isNativeApp ? `calc(${statusBarHeight}px + 0.5rem)` : '1.5rem',
          }}
        >
          <div className="w-[50px] flex justify-center">
            <button
              onClick={handleDashboardClick}
              className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
            >
              <img src="/logo-icon-white.svg" alt="Zentime Logo" className="h-7 w-auto" />
              <div
                className="font-bold tracking-wider text-sm"
                style={{ color: 'var(--text-primary)' }}
              >
                ZEN
              </div>
            </button>
          </div>

          {/* Expanded icon underneath app name */}
          <div className="mt-3">
            <button
              onClick={handleExpand}
              className="hidden lg:block p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-3">
            <NotificationCenter />
          </div>
        </div>

        {/* Overview button with eye icon */}
        <div className="w-full mt-4 mb-3 flex justify-center">
          <div className="relative inline-flex">
            <button
              onClick={handleDashboardClick}
              className="w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors shadow-sm"
              style={{
                opacity: isDashboardActive ? 1 : 0.4,
                filter: isDashboardActive ? 'none' : 'grayscale(80%) contrast(0.9) brightness(0.9)',
                transition: 'opacity 0.2s, filter 0.2s',
              }}
              onMouseOver={e => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.filter = 'none';
              }}
              onMouseOut={e => {
                if (!isDashboardActive) {
                  e.currentTarget.style.opacity = '0.4';
                  e.currentTarget.style.filter = 'grayscale(80%) contrast(0.9) brightness(0.9)';
                } else {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.filter = 'none';
                }
              }}
              title="Overview"
              onMouseEnter={() => setHoveredProject('overview')}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <Eye size={16} className="text-white" />
            </button>

            {/* Tooltip on hover using ReactDOM portal */}
            {hoveredProject === 'overview' &&
              ReactDOM.createPortal(
                <div
                  className="fixed left-12 bg-white text-sm py-1.5 px-3 rounded-md shadow-lg whitespace-nowrap z-[9999] border border-gray-200 font-medium"
                  style={{
                    color: 'var(--text-primary)',
                    top: '188px',
                  }}
                >
                  Overview
                </div>,
                document.body
              )}
          </div>
        </div>

        {/* Project icons with scrolling and animations */}
        <div className="w-full flex-1 overflow-hidden">
          {/* Special container for the selected project when sidebar is not hovered */}
          {!isSidebarHovered && project && (
            <div
              className="w-full flex justify-center mb-3 relative"
              style={{
                // Animation for selected project coming in from right
                animation: 'collapsedSidebarSlideFromRight 0.25s forwards',
              }}
            >
              <div
                className="relative inline-flex"
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <button
                  onClick={() => {}} // No action needed here since already selected
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  title={project.name}
                >
                  {project.avatar_url ? (
                    <img
                      src={project.avatar_url}
                      alt={project.name}
                      className="w-8 h-8 rounded-full object-cover shadow-md"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 shadow-md flex items-center justify-center">
                      <Folder size={18} className="text-gray-400" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Main container for all projects when sidebar is hovered */}
          <div
            className="h-full flex flex-col items-center overflow-y-auto px-2 pb-2 scrollbar-thin"
            style={{
              height: 'calc(100vh - 230px)' /* Updated to account for Overview button */,
              maxHeight: 'calc(100vh - 230px)',
              overflowY: 'auto',
            }}
          >
            {/* Always render all non-archived projects, but animate them based on sidebar hover state */}
            {nonArchivedProjects.map((p, index) => {
              // Active project gets special positioning
              const isActiveProject = project?.id === p.id;

              return (
                <div
                  key={p.id}
                  className="relative mb-3 w-full flex justify-center"
                  data-hover-id={p.id}
                  style={{
                    // Optimize transform performance to prevent layout shifts
                    willChange: 'transform, opacity',

                    // Custom transition with conditional animation
                    transition: 'all 0.2s cubic-bezier(0.25, 1, 0.5, 1)',

                    // For regular hover state appearance:
                    // - All projects including active disappear to the left when not hovered
                    // - All projects reappear with staggered animation when hovered
                    transform: !isSidebarHovered
                      ? 'translateX(-100%)' // All projects disappear to the left
                      : 'translateX(0)', // All projects appear from the left

                    // Standard positioning for all projects in this list
                    position: 'relative',

                    // When not hovered, hide all projects (even active one)
                    opacity: !isSidebarHovered ? 0 : 1,
                    pointerEvents: !isSidebarHovered ? 'none' : 'auto',

                    // Staggered delay only on appearance
                    transitionDelay: isSidebarHovered ? `${index * 0.04}s` : '0s',
                  }}
                >
                  <div
                    className="relative inline-flex"
                    onMouseEnter={() => setHoveredProject(p.id)}
                    onMouseLeave={() => setHoveredProject(null)}
                  >
                    <button
                      onClick={() => handleProjectClick(p.id)}
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      title={p.name}
                      style={{
                        opacity: isActiveProject ? 1 : 0.4,
                        filter: isActiveProject
                          ? 'none'
                          : 'grayscale(80%) contrast(0.9) brightness(0.9)',
                        transition: 'opacity 0.2s, filter 0.2s',
                      }}
                      onMouseOver={e => {
                        if (!isActiveProject) {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.filter = 'none';
                        }
                      }}
                      onMouseOut={e => {
                        if (!isActiveProject) {
                          e.currentTarget.style.opacity = '0.4';
                          e.currentTarget.style.filter =
                            'grayscale(80%) contrast(0.9) brightness(0.9)';
                        }
                      }}
                    >
                      {p.avatar_url ? (
                        <img
                          src={p.avatar_url}
                          alt={p.name}
                          className={`w-8 h-8 rounded-full object-cover ${isActiveProject ? 'shadow-md' : 'shadow'}`}
                        />
                      ) : (
                        <div
                          className={`w-8 h-8 rounded-full ${isActiveProject ? 'bg-gray-700 shadow-md' : 'bg-gray-800 shadow'} flex items-center justify-center transition-colors`}
                        >
                          <Folder
                            size={18}
                            className={`${isActiveProject ? 'text-gray-400' : 'text-gray-600'} transition-colors`}
                          />
                        </div>
                      )}
                    </button>

                    {/* Project name tooltip on hover using ReactDOM portal */}
                    {hoveredProject === p.id &&
                      ReactDOM.createPortal(
                        <div
                          className="fixed left-14 bg-white text-sm py-1.5 px-3 rounded-md shadow-lg whitespace-nowrap z-[9999] border border-gray-200 font-medium"
                          style={{
                            color: 'var(--text-primary)',
                            // Calculate tooltip position based on whether sidebar is hovered
                            top:
                              !isSidebarHovered && isActiveProject
                                ? '186px' // Position just below overview icon when active project is moved up
                                : `calc(186px + ${index * 44 + 44}px)`, // Regular position in the list
                          }}
                        >
                          {p.name}
                        </div>,
                        document.body
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
